import type React from "react"
import { useEffect, useRef, useState } from "react"
import { t } from "@lingui/core/macro"
import { isiPad, isSamsungTablet } from "@multimediallc/web-utils/modernizr"
import { Sizes } from "../../../interfaces/sizing"
import { mergeClasses } from "../../../utils/css"
import { Typography } from "../../common"
import { CloseButton } from "../../common/atoms/CloseButton"
import { InfoIconTooltip } from "../../common/molecules/InfoIconTooltip"
import { FlaggedChatTooltip } from "../FlaggedChatTooltip"
import "./DesktopFlaggedChat.scss"

interface FlaggedChatProps extends React.ComponentPropsWithoutRef<"div"> {
    chatDiv: HTMLDivElement
    reasons: string[]
    isPureChat?: boolean
    scrollCallback: () => void
    removeCallback: () => void
}

export function DesktopFlaggedChat({
    chatDiv,
    reasons,
    isPureChat,
    scrollCallback,
    removeCallback,
    ...props
}: FlaggedChatProps) {
    const ref = useRef(null)
    const [isOpen, setIsOpen] = useState(true)
    const [closeTooltip, setCloseTooltip] = useState(false)

    useEffect(() => {
        // scroll callback has the logic to scroll chat to bottom based on whether the user has scrolled up manually
        // and if the chat div's height was changed.
        // We need a callback here to let the parent handle scroll calculations after this component is finished rendering.
        scrollCallback()
    }, [scrollCallback])

    if (!isOpen) {
        return <></>
    }

    const className = mergeClasses(
        "FlaggedChat",
        isPureChat ? "purechat" : "splitchat",
    )

    return (
        <div className={className} {...props} ref={ref}>
            <div>
                <div className="FlaggedChat__header">
                    <InfoIconTooltip
                        className="FlaggedChat__tooltip_icon"
                        tooltipClassName="FlaggedChat__tooltip"
                        closeTooltip={closeTooltip}
                        parentRef={ref}
                    >
                        <Typography
                            component="h2"
                            size={Sizes.smpx}
                            weight="bold"
                            className="FlaggedChat__tooltip_header"
                        >
                            {t`This message was rejected.`}
                        </Typography>
                        {(isiPad() || isSamsungTablet()) && (
                            <CloseButton
                                className="FlaggedChat__tooltip_close-btn"
                                onClick={() => setCloseTooltip(true)}
                                size="14"
                            />
                        )}
                        <div className="FlaggedChat__content">
                            <FlaggedChatTooltip reasons={reasons} />
                        </div>
                    </InfoIconTooltip>
                    <Typography
                        component="h2"
                        size={Sizes.smpx}
                        weight="normal"
                        className="FlaggedChat__info"
                    >
                        {t`This message was rejected due to ChaturSafe filter.`}
                    </Typography>
                </div>
                <div>
                    <CloseButton
                        className="FlaggedChat__close"
                        onClick={() => {
                            removeCallback()
                            setIsOpen(false)
                        }}
                        size="10"
                    />
                </div>
            </div>
            <div
                ref={(ref) => {
                    ref?.appendChild(chatDiv)
                }}
            />
        </div>
    )
}
